import { useEffect } from 'react'
import { create } from 'zustand'

/**
 * Which full-screen overlay is showing, if any.
 *
 * This lives beside the app store rather than inside it because the app store
 * is defined as "only what the URL holds", and the URL codec in
 * `state/urlState.ts` is frozen - the parameter names appear in links people
 * have already shared. An overlay is chrome, not a filter, so it gets its own
 * tiny store and rides on the fragment instead.
 */

export const OVERLAY_ROUTES = {
  about: '#/about',
  methodology: '#/methodology',
} as const

export type OverlayId = keyof typeof OVERLAY_ROUTES

interface OverlayState {
  readonly overlay: OverlayId | null
  open(overlay: OverlayId): void
  close(): void
}

export const useOverlayStore = create<OverlayState>((set) => ({
  overlay: null,
  open: (overlay) => set({ overlay }),
  close: () => set({ overlay: null }),
}))

const overlayForHash = (hash: string): OverlayId | null => {
  const found = (Object.keys(OVERLAY_ROUTES) as OverlayId[]).find(
    (id) => OVERLAY_ROUTES[id] === hash,
  )
  return found ?? null
}

/**
 * Keeps the fragment and the overlay in step, so `…/#/about` is a link somebody
 * can send. Mounted once, by the footer.
 *
 * The fragment is a mirror, not the source of truth: `App`'s URL sync rewrites
 * the address bar from `pathname + search` whenever a filter changes, which
 * drops any fragment. Losing the fragment therefore loses the shareable link
 * but never closes an open overlay.
 */
export const useOverlayHashSync = (): void => {
  const open = useOverlayStore((state) => state.open)
  const close = useOverlayStore((state) => state.close)

  useEffect(() => {
    const apply = (): void => {
      const next = overlayForHash(window.location.hash)
      if (next === null) close()
      else open(next)
    }

    apply()
    window.addEventListener('hashchange', apply)
    return () => window.removeEventListener('hashchange', apply)
  }, [open, close])

  useEffect(
    () =>
      useOverlayStore.subscribe((state) => {
        const hash = state.overlay === null ? '' : OVERLAY_ROUTES[state.overlay]
        if (window.location.hash === hash) return
        const { pathname, search } = window.location
        window.history.replaceState(null, '', `${pathname}${search}${hash}`)
      }),
    [],
  )
}
